import axios from 'axios';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

export function CreerProjet() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  
  const [loading, setLoading] = useState(true);  // FIX: true au lieu de false
  const [client, setClient] = useState(null);
  const [employees, setEmployees] = useState([]);

  const [formData, setFormData] = useState({
    nom_dossier: '',
    type_contrat: 'peinture',
    departement: 'PRQC',
    date_debut_prevue: '',
    adresse_travaux: '',
    ville_travaux: '',
    code_postal_travaux: '',
    utilise_adresse_client_travaux: false,
    representant_id: '',
    representant_nom: '',
  });

  useEffect(() => {
    if (clientId) {
      fetchClient();
      fetchEmployees();
    } else {
      toast.error('Aucun client sélectionné');
      navigate('/crm/clients');
    }
  }, []);

  const fetchClient = async () => {
    setLoading(true);  // FIX: Ajouter au début
    try {
      const response = await axios.get(`${API_URL}/api/crm/clients/${clientId}`);
      if (response.status !== 200) throw new Error('Client non trouvé');
      const data = response.data;
      setClient(data);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors du chargement du client');
      navigate('/crm/clients');
    } finally {
      setLoading(false);  // FIX: Ajouter finally
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees`);
      if (response.status !== 200) throw new Error('Erreur');
      const data = response.data;
      setEmployees(data.employees || []);
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const handleUtiliserAdresseClient = (checked) => {
    if (checked && client) {
      setFormData((prev) => ({
        ...prev,
        utilise_adresse_client_travaux: true,
        adresse_travaux: client.adresse_client,
        ville_travaux: client.ville_client,
        code_postal_travaux: client.code_postal_client,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        utilise_adresse_client_travaux: false,
      }));
    }
  };

  const handleRepresentantChange = (employeeId) => {
    const employee = employees.find((e) => e.id === employeeId);
    if (employee) {
      setFormData((prev) => ({
        ...prev,
        representant_id: employeeId,
        representant_nom: `${employee.prenom} ${employee.nom}`,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nom_dossier || !formData.adresse_travaux) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData.departement) {
      toast.error('Le département est obligatoire — il détermine les documents joints à la soumission');
      return;
    }

    setLoading(true);

    try {
      const payload = {
        client_id: clientId,
        client_nom: client.nom,
        nom_dossier: formData.nom_dossier,
        type_contrat: formData.type_contrat,
        departement: formData.departement,
        adresse_travaux: formData.adresse_travaux,
        ville_travaux: formData.ville_travaux,
        code_postal_travaux: formData.code_postal_travaux,
        utilise_adresse_client_travaux: formData.utilise_adresse_client_travaux,
        representant_id: formData.representant_id || null,
        representant_nom: formData.representant_nom || null,
        date_debut_prevue: formData.date_debut_prevue || null,
      };

      const response = await axios.post(`${API_URL}/api/crm/dossiers`, payload);

      if (response.status !== 200) throw new Error('Erreur');

      const data = response.data;
      toast.success(`Projet ${data.numero_dossier} créé avec succès!`);
      navigate(`/crm/projets/${data.dossier_id}`);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la création du projet');
    } finally {
      setLoading(false);
    }
  };

  // FIX: Ajouter un guard de chargement
  if (loading || !client) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate(`/crm/clients/${clientId}`)} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour à la fiche client
        </Button>
        <h1 className="text-3xl font-bold text-gray-900">Nouveau Projet</h1>
        <p className="text-gray-600 mt-1">
          Client : <span className="font-semibold">{client.nom}</span>
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="space-y-6">
          {/* Informations Projet */}
          <Card>
            <CardHeader>
              <CardTitle>Informations du Projet</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="nom_dossier">
                  Nom du Projet <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nom_dossier"
                  placeholder="Ex: Peinture Façade Extérieure"
                  value={formData.nom_dossier}
                  onChange={(e) => setFormData({ ...formData, nom_dossier: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="type_contrat">Type de Contrat</Label>
                  <Select
                    value={formData.type_contrat}
                    onValueChange={(val) => setFormData({ ...formData, type_contrat: val })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="peinture">Peinture</SelectItem>
                      <SelectItem value="location">Location</SelectItem>
                      <SelectItem value="a_l_heure">À l'heure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="departement">Département <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.departement}
                    onValueChange={(val) => setFormData({ ...formData, departement: val })}
                    required
                  >
                    <SelectTrigger className={!formData.departement ? 'border-red-300' : ''}>
                      <SelectValue placeholder="Choisir un département..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PRQC">PRQC — Peinture</SelectItem>
                      <SelectItem value="LMDS">LMDS</SelectItem>
                      <SelectItem value="LOCATION">LOCATION — Équipement</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-0.5">Détermine les documents joints à la soumission</p>
                </div>

                <div>
                  <Label htmlFor="date_debut_prevue">Date de création projet</Label>
                  <Input
                    id="date_debut_prevue"
                    type="date"
                    value={formData.date_debut_prevue}
                    onChange={(e) => setFormData({ ...formData, date_debut_prevue: e.target.value })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Adresse des Travaux */}
          <Card>
            <CardHeader>
              <CardTitle>Adresse des Travaux</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="utilise_adresse_client"
                  checked={formData.utilise_adresse_client_travaux}
                  onCheckedChange={handleUtiliserAdresseClient}
                />
                <Label htmlFor="utilise_adresse_client" className="cursor-pointer">
                  Utiliser l'adresse du client
                </Label>
              </div>

              <div>
                <Label htmlFor="adresse_travaux">
                  Adresse <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="adresse_travaux"
                  placeholder="123 Rue Principale"
                  value={formData.adresse_travaux}
                  onChange={(e) => setFormData({ ...formData, adresse_travaux: e.target.value })}
                  disabled={formData.utilise_adresse_client_travaux}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ville_travaux">
                    Ville <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ville_travaux"
                    placeholder="Montréal"
                    value={formData.ville_travaux}
                    onChange={(e) => setFormData({ ...formData, ville_travaux: e.target.value })}
                    disabled={formData.utilise_adresse_client_travaux}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="code_postal_travaux">
                    Code Postal <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="code_postal_travaux"
                    placeholder="H1A 1A1"
                    value={formData.code_postal_travaux}
                    onChange={(e) => setFormData({ ...formData, code_postal_travaux: e.target.value })}
                    disabled={formData.utilise_adresse_client_travaux}
                    required
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assignation Représentant */}
          <Card>
            <CardHeader>
              <CardTitle>Assignation du Représentant</CardTitle>
            </CardHeader>
            <CardContent>
              <div>
                <Label htmlFor="representant">Représentant</Label>
                <Select
                  value={formData.representant_id}
                  onValueChange={handleRepresentantChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un représentant" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map((emp) => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.prenom} {emp.nom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-gray-500 mt-2">
                  Le chef d'équipe et le chargé de projet seront assignés après la signature du contrat.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(`/crm/clients/${clientId}`)}>
              Annuler
            </Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Création...' : 'Créer le Projet'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
